import { useEffect, useState } from 'react';
import { useViewer } from '@/features/session';
import { ACTIVATION_POLL_MS, ACTIVATION_TIMEOUT_MS } from '../constants';
import type { PaidPlanId } from '../types';

export type ActivationStatus = 'off' | 'waiting' | 'active' | 'timeout';

/**
 * After Paddle takes the payment the plan turns on when its webhook arrives, so the page asks the
 * server every 2 s (up to a minute) until the subscription shows the plan.
 */
export function usePlanActivation(plan: PaidPlanId | null): ActivationStatus {
  const { workspace, refreshViewer } = useViewer();
  const [isTimedOut, setIsTimedOut] = useState(false);
  const subscription = workspace?.billing.subscription ?? null;
  const isActive =
    plan !== null &&
    subscription?.plan === plan &&
    (subscription.status === 'ACTIVE' || subscription.status === 'TRIALING');

  useEffect(() => {
    if (!plan || isActive) return undefined;
    setIsTimedOut(false);
    const poll = window.setInterval(() => void refreshViewer(), ACTIVATION_POLL_MS);
    const stop = window.setTimeout(() => {
      window.clearInterval(poll);
      setIsTimedOut(true);
    }, ACTIVATION_TIMEOUT_MS);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(stop);
    };
  }, [plan, isActive, refreshViewer]);

  if (!plan) return 'off';
  if (isActive) return 'active';
  return isTimedOut ? 'timeout' : 'waiting';
}
